import { useState, useCallback, useMemo, useEffect } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import api from "../utils/api/axiosInstance";
import { useToast } from "../contexts/ToastContext";

const FEED_SOURCE_FILTER_STORAGE_KEY = "simpary_feed_source_filter";
const VALID_FEED_SOURCE_FILTERS = new Set(["forYou", "following"]);

const getStoredFeedSourceFilter = () => {
  try {
    const saved = localStorage.getItem(FEED_SOURCE_FILTER_STORAGE_KEY);
    if (saved && VALID_FEED_SOURCE_FILTERS.has(saved)) {
      return saved;
    }
  } catch (err) {
    console.error("Failed to read feed source filter:", err);
  }
  return "forYou";
};

export function useFeed(options = {}) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { enabled = true, initialSourceFilter } = options;
  const [activeFilter, setActiveFilter] = useState(() => {
    if (initialSourceFilter && VALID_FEED_SOURCE_FILTERS.has(initialSourceFilter)) {
      return initialSourceFilter;
    }
    return getStoredFeedSourceFilter();
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryKey = useMemo(() => ["feed", activeFilter], [activeFilter]);

  const feedQuery = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const current = pageParam || { page: 1, cursor: null };
      const res = await api.get("/posts/feed", {
        params: {
          page: current.page,
          limit: 20,
          filter: activeFilter,
          ...(current.cursor ? { cursor: current.cursor } : {}),
        },
      });
      return res.data;
    },
    initialPageParam: { page: 1, cursor: null },
    enabled,
    staleTime: 30 * 1000,
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage?.pagination || {};
      if (!pagination.hasMore) return undefined;
      return {
        page: (pagination.page || allPages.length) + 1,
        cursor: pagination.nextCursor || null,
      };
    },
    refetchOnWindowFocus: true,
  });

  const posts = useMemo(
    () =>
      (feedQuery.data?.pages || []).flatMap((pageData) =>
        Array.isArray(pageData?.posts) ? pageData.posts : [],
      ),
    [feedQuery.data],
  );

  useEffect(() => {
    if (!feedQuery.error) return;
    const message = feedQuery.error.response?.data?.message || "Failed to load feed";
    showToast({ message, type: "error" });
  }, [feedQuery.error, showToast]);

  useEffect(() => {
    if (!VALID_FEED_SOURCE_FILTERS.has(activeFilter)) return;
    try {
      localStorage.setItem(FEED_SOURCE_FILTER_STORAGE_KEY, activeFilter);
    } catch (err) {
      console.error("Failed to save feed source filter:", err);
    }
  }, [activeFilter]);

  const fetchFeed = useCallback(
    async (_pageNum = 1, options = {}) => {
      const { isRefresh = false, filter: filterOverride } = options;
      if (!enabled) return;

      const effectiveFilter =
        filterOverride && VALID_FEED_SOURCE_FILTERS.has(filterOverride)
          ? filterOverride
          : activeFilter;

      if (filterOverride && filterOverride !== activeFilter) {
        setActiveFilter(filterOverride);
      }

      try {
        if (isRefresh) setIsRefreshing(true);
        await queryClient.invalidateQueries({
          queryKey: ["feed", effectiveFilter],
        });
      } finally {
        if (isRefresh) setIsRefreshing(false);
      }
    },
    [activeFilter, queryClient, enabled],
  );

  const loadMore = useCallback(() => {
    if (
      feedQuery.hasNextPage &&
      !feedQuery.isFetchingNextPage &&
      !feedQuery.isPending &&
      !isRefreshing
    ) {
      feedQuery.fetchNextPage();
    }
  }, [feedQuery, isRefreshing]);

  const handleFilterChange = useCallback(
    (newFilter) => {
      if (newFilter === activeFilter) return;
      setActiveFilter(newFilter);
    },
    [activeFilter],
  );

  const setPosts = useCallback(
    (updater) => {
      queryClient.setQueryData(queryKey, (prev) => {
        if (!prev) return prev;
        const currentPosts = (prev.pages || []).flatMap((pageData) => pageData.posts || []);
        const nextPosts = typeof updater === "function" ? updater(currentPosts) : updater;
        const firstPage = prev.pages?.[0] || {};
        return {
          ...prev,
          pages: [{ ...firstPage, posts: nextPosts }],
          pageParams: [prev.pageParams?.[0] || { page: 1, cursor: null }],
        };
      });
    },
    [queryClient, queryKey],
  );

  // Optimistic helpers exposed for the page to use
  const prependPost = useCallback(
    (newPost) => {
      queryClient.setQueryData(queryKey, (prev) => {
        if (!prev || !prev.pages?.length) {
          return {
            pageParams: [{ page: 1, cursor: null }],
            pages: [{ posts: [newPost], pagination: { page: 1, hasMore: false } }],
          };
        }

        const firstPage = prev.pages[0];
        return {
          ...prev,
          pages: [
            { ...firstPage, posts: [newPost, ...(firstPage.posts || [])] },
            ...prev.pages.slice(1),
          ],
        };
      });
    },
    [queryClient, queryKey],
  );

  const updatePost = useCallback(
    (postId, updater) => {
      queryClient.setQueryData(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: (prev.pages || []).map((pageData) => ({
            ...pageData,
            posts: (pageData.posts || []).map((p) => (p._id === postId ? updater(p) : p)),
          })),
        };
      });
    },
    [queryClient, queryKey],
  );

  const removePost = useCallback(
    (postId) => {
      queryClient.setQueryData(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: (prev.pages || []).map((pageData) => ({
            ...pageData,
            posts: (pageData.posts || []).filter(
              (p) => String(p._id) !== String(postId),
            ),
          })),
        };
      });
    },
    [queryClient, queryKey],
  );

  return {
    // State
    posts,
    activeFilter,
    loading: feedQuery.isPending,
    loadingMore: feedQuery.isFetchingNextPage,
    hasMore: Boolean(feedQuery.hasNextPage),
    isRefreshing,
    page: feedQuery.data?.pages?.length || 1,
    error: feedQuery.error?.response?.data?.message || (feedQuery.error ? "Failed to load feed" : null),

    // Actions
    fetchFeed,
    loadMore,
    handleFilterChange,
    setPosts,

    // Mutation helpers
    prependPost,
    updatePost,
    removePost,
  };
}
